// EditPostModal.js

import { useState, useEffect, useRef } from "react";
import { usePage, useForm, router } from "@inertiajs/react";
import LinkPreview from "@/Components/LinkPreview";
import AddGiphy from "@/Components/AddGiphy";
import UserOne from "../../images/user/user-00.png";
import GiphyLogo from "../../images/giphy-logo.png";
import PostFileDropzone from "@/Components/Forms/PostFileDropzone";
import { fetchPostEmbedData } from "@/Services/PostEmbedService";
import { useQueryClient } from "@tanstack/react-query";
import ApiService from "@/Services/ApiService";
import { displayUserName } from "@/utils/displayUserName";

const EditPostModal = ({ isOpen, onClose, post }) => {
    const { props: sharedProps } = usePage();
    const currentUser = sharedProps.auth.user;
    const { giphyApiKey, buckets, tenantSlug } = sharedProps;

    const profileImagesBucket = buckets["profile_images"].name;
    const profileImagesRegion = buckets["profile_images"].region;
    const profileImageBaseUrl = `https://${profileImagesBucket}.s3.${profileImagesRegion}.amazonaws.com/${tenantSlug}/`;

    const [uploadImagesForm, setUploadImagesForm] = useState(false);
    const [giphyPostOption, setGiphyPostOption] = useState(false);
    const dropzoneRef = useRef();

    const { data, setData, processing, errors, reset } = useForm({
        message: "",
        images: [],
        embed: null,
    });

    const queryClient = useQueryClient();

    useEffect(() => {
        if (post) {
            const simulatedFiles = (post.existingImageUrls || []).map(
                (url) => ({
                    name: url.split("/").pop(),
                    preview: url,
                    isExisting: true,
                }),
            );

            setData((prev) => ({
                ...prev,
                message: post.message || "",
                embed: post.embed || null,
                images: simulatedFiles,
            }));

            if (post.embed?.giphy_image_url) {
                setGiphyPostOption(true);
                setUploadImagesForm(false);
            } else if (post.existingImageUrls?.length > 0) {
                setUploadImagesForm(true);
                setGiphyPostOption(false);
            }
        }
    }, [post]);

    const handleUpdatePost = async (e) => {
        e.preventDefault();

        const formData = new FormData();

        formData.append("_method", "PUT"); // simulate PUT request
        formData.append("message", data.message);

        const expectedEmbedKeys = [
            "giphy",
            "href",
            "title",
            "description",
            "imageUrl",
            "youtubeId",
            "youtubeImageUrl",
            "giphyImageUrl",
            "portrait",
        ];

        if (data.embed && typeof data.embed === "object") {
            expectedEmbedKeys.forEach((key) => {
                const value = key in data.embed ? data.embed[key] : "";
                formData.append(`embed[${key}]`, value);
            });
        }

        // Handle files and existing image URLs
        const filesToUpload = data.images.filter((img) => img instanceof File);
        const existingUrls = data.images
            .filter((img) => !(img instanceof File))
            .map((img) => img.preview);

        filesToUpload.forEach((file, index) => {
            formData.append(`images[${index}]`, file);
        });

        existingUrls.forEach((url, index) => {
            formData.append(`existing_image_urls[${index}]`, url);
        });

        try {
            const response = await ApiService.post(
                route("users.posts.update", { id: post.id }),
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                },
            );

            const updatedPost = response.post;

            if (updatedPost) {
                // Optimistically update cache
                queryClient.setQueryData(["posts"], (oldData) => {
                    if (!oldData) return oldData;
                    return {
                        ...oldData,
                        pages: oldData.pages.map((page) => ({
                            ...page,
                            data: page.data.map((p) =>
                                p.id === updatedPost.id ? updatedPost : p,
                            ),
                        })),
                    };
                });
                // Ensure UI is in sync with server
                queryClient.invalidateQueries(["posts"]);
            }

            onClose();
        } catch (error) {
            if (error.response?.data?.errors) {
                console.error("Validation errors:", error.response.data.errors);
            } else {
                console.error("Update error:", error);
            }
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="w-full max-w-2xl mx-auto bg-white dark:bg-boxdark shadow-lg rounded-lg p-6 relative max-h-[90vh]">
                <div className="overflow-y-auto max-h-[75vh] pr-2">
                    {/* Header */}
                    <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-3">
                            <img
                                className="h-10 w-10 rounded-full object-cover"
                                src={
                                    currentUser?.avatar_image_file_name
                                        ? profileImageBaseUrl +
                                          currentUser.avatar_image_file_name
                                        : currentUser?.login_image_url ||
                                          UserOne
                                }
                                alt="User"
                            />
                            <div>
                                <p className="font-semibold text-gray-900 dark:text-white">
                                    {displayUserName(currentUser)}
                                </p>
                                <p className="text-sm text-gray-500">
                                    Edit Post
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 text-xl"
                        >
                            ✕
                        </button>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleUpdatePost}>
                        <textarea
                            name="message"
                            rows={2}
                            placeholder="Update your message..."
                            className="text-xl ml-1 w-full dark:border-strokedark dark:bg-form-input dark:text-white rounded-lg p-4 resize-y focus:outline-none focus:ring-2 focus:ring-blue-400"
                            value={data.message}
                            onChange={async (e) => {
                                const value = e.target.value;
                                setData((prev) => ({
                                    ...prev,
                                    message: value,
                                }));

                                const embed = await fetchPostEmbedData(value);
                                if (!embed) return;

                                const hasImages =
                                    (data.images || []).length > 0;

                                const hasGiphyEmbed = data.embed?.giphyImageUrl;

                                if (hasImages || hasGiphyEmbed) {
                                    const confirmed = window.confirm(
                                        "Adding a link preview will remove your selected images and any GIF embed. Continue?",
                                    );
                                    if (!confirmed) return;

                                    dropzoneRef.current?.clearFiles();
                                    setUploadImagesForm(false);
                                    setGiphyPostOption(false);
                                    setData((prev) => ({
                                        ...prev,
                                        images: [],
                                        embed: null,
                                    }));
                                }

                                setData((prev) => ({
                                    ...prev,
                                    embed: embed,
                                }));
                            }}
                        />
                        {errors.message && (
                            <p className="text-red mt-1 text-sm">
                                {errors.message}
                            </p>
                        )}

                        {data.embed && (
                            <div className="relative ml-3 mt-4 w-98 z-10 ">
                                <LinkPreview
                                    setData={setData}
                                    {...data.embed}
                                />
                            </div>
                        )}

                        <div className="mt-4 px-4 py-3 border rounded-lg flex justify-between items-center bg-gray-50 dark:bg-meta-4">
                            <span className="text-gray-600 dark:text-white text-xl">
                                Add to your post 👉
                            </span>
                            <div className="flex gap-4">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setUploadImagesForm((prev) => !prev);
                                        setGiphyPostOption(false);

                                        // Clear the Dropzone UI (in case you toggled off and back on)
                                        dropzoneRef.current?.clearFiles();

                                        setData((prev) => ({
                                            ...prev,
                                            embed: null,
                                        }));
                                    }}
                                    title="Add Photo"
                                >
                                    <span className="text-indigo-500 text-xl">
                                        📷
                                    </span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (data.images.length > 0) {
                                            const confirmClear = window.confirm(
                                                "Selecting a GIF will remove your currently selected images. Do you want to continue?",
                                            );
                                            if (!confirmClear) return;
                                        }

                                        setGiphyPostOption(!giphyPostOption);
                                        setUploadImagesForm(false);
                                        setData("images", []);
                                        setData("embed", null);
                                    }}
                                    title="Add GIF"
                                >
                                    <img
                                        src={GiphyLogo}
                                        alt="Giphy Logo"
                                        className="h-5 w-4"
                                    />
                                </button>
                            </div>
                        </div>

                        {uploadImagesForm && (
                            <div className="mt-4">
                                <PostFileDropzone
                                    ref={dropzoneRef}
                                    fileList={data.images}
                                    onFileAdd={(file) => {
                                        setData((prev) => ({
                                            ...prev,
                                            images: [
                                                ...(prev.images || []),
                                                file,
                                            ],
                                        }));
                                    }}
                                    onFileRemove={(file) => {
                                        setData((prev) => ({
                                            ...prev,
                                            images: prev.images.filter(
                                                (f) => f.name !== file.name,
                                            ),
                                        }));
                                    }}
                                />
                            </div>
                        )}

                        {giphyPostOption && (
                            <div className="mt-4">
                                <AddGiphy
                                    apiKey={giphyApiKey}
                                    setGiphyPostOption={setGiphyPostOption}
                                    giphyPostOption={giphyPostOption}
                                    onGifSelect={(gif) => {
                                        setData("embed", {
                                            giphy: true,
                                            giphyImageUrl:
                                                gif.images.fixed_height.url,
                                        });
                                    }}
                                />
                            </div>
                        )}

                        <div className="mt-6">
                            <button
                                type="submit"
                                disabled={processing}
                                className="w-full py-3 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold hover:opacity-90 transition"
                            >
                                Update
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default EditPostModal;
